import {IProduct} from "../../interfaces/IProduct";
// @ts-ignore
import ReactStars from "react-rating-stars-component";
import React, {useEffect, useState} from "react";
import styled from "styled-components";
import Button from "../Button";
import {useMessages} from "../../hooks/useMessages";
import {useCountryLang} from "../../hooks/useCountryLang";
import {getTheme} from "../../shared/themeUtils";

const ReviewsContain = styled.div` 
  margin: ${getTheme("space.3")} 0;
`;

const OverallRating = styled.div`
  margin-bottom: ${getTheme("space.2")};

  & > div {
    display: flex;

    .overall-rating {
      font-size: 64px;
      margin-right: ${getTheme("space.2")};
    }
    
    .overall-stars {
      display: flex;
      justify-content: center;
        align-items: center;
    }

    small {
      font-weight: bold;
        margin-left: ${getTheme("space.1")};
    }
  }
  
  .star-placeholder {
    height: 32px;
    width: 160px;
    background-color: #f0f0f0;
    margin-bottom: 4px;
  }
`;

const Reviews = styled.div`
  .review {
    margin-bottom: ${getTheme("space.2")};
    border: 1px solid ${getTheme("colors.border")};
    border-radius: ${getTheme("borderRadius")};
    padding: ${getTheme("space.2")};
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    
    .right-side {
      min-width: 280px;
      padding-left: ${getTheme("space.2")};
    }

    .user-info {
      display: flex;
      align-items: center;

      .image {
        display: flex;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: ${getTheme("colors.primary")};
          color: ${getTheme("colors.white")};
        justify-content: center;
        align-items: center;
        margin-right: ${getTheme("space.1")};
      }

      p {
        margin: 0;

        &.date {
          font-size: 14px;
          color: ${getTheme("colors.secondaryFont")};
        }
      }
    }
    
    .rating {
      margin: ${getTheme("space.1")} 0;
    }

    .review-content {
      display: flex;
      flex-wrap: wrap;

      .review-text {
        flex: 1;
      }
    }
    
    .individual-ratings {
        .individual-rating {
          display: flex;
          border-bottom: 1px solid ${getTheme("colors.border")};
          justify-content: space-between;
          align-items: center;
          
          .name {
            font-weight: 500;
          }

          &:last-of-type {
            border-bottom: none;
          }
        }
      }
      
      @media (max-width: ${getTheme("breakpoints.2")}) {
        flex-direction: column;
        .right-side {
          width: 100%;
          padding-left: 0;
        }
      }
  }
  
  .star-placeholder-small {
    height: 24px;
    width: 120px;
    background-color: #f0f0f0;
    margin-bottom: 4px;
  }
  
  .star-placeholder-tiny {
    height: 20px;
    width: 100px;
    background-color: #f0f0f0;
    margin-bottom: 2px;
  }
`;

const SeeAllReviews = styled.div`
  padding: ${getTheme("space.2")} 0;
  text-align: center;
`;

// ClientOnly wrapper for ReactStars to prevent hydration mismatch
const StarRating = ({ rating, size = 24 }: { rating: number, size?: number }) => {
  const [isMounted, setIsMounted] = useState(false);
  
  useEffect(() => {
    setIsMounted(true);
  }, []);
  
  if (!isMounted) {
    return <div className={size > 20 ? (size > 30 ? "star-placeholder" : "star-placeholder-small") : "star-placeholder-tiny"}></div>;
  }
  
  return (
    <ReactStars
      count={5}
      size={size}
      activeColor="#ffd700"
      edit={false}
      isHalf={true}
      value={rating}
    />
  );
};

interface ProductReviewsProps {
  product: IProduct,
}

function filterReviewsByLanguage(countryLang: string | undefined, productReviews: any[]) {
  return productReviews.filter(review => review.languageCode === countryLang);
}

const ProductReviews = (props: ProductReviewsProps) => {
  const countryLang = useCountryLang()
  const {product} = props;

  //Show all language reviews
  const [showAllLangs, setShowAllLangs] = useState(false)

  // Website language reviews
  const websiteLangReviews = filterReviewsByLanguage(countryLang?.slice(3, 5), product.productReviews);
  // All reviews to show taking into consideration if different langs are shown
  const reviews = showAllLangs ? product.productReviews : websiteLangReviews;
  const hasDifferentLangReviews = reviews.length != product.productReviews.length;
  //Number of reviews to show
  const [defaultReviews, setDefaultReviews] = useState(2)
  const messagesQuery = useMessages()

  const handleShowAllLangs = () => {
    //Set also defaultReviews to show all reviews
    setDefaultReviews(product.productReviews.length)
    setShowAllLangs(true)
  };

  const getDate = (date: string) => {
    const d = new Date(date.split(",")[0]);
    return d.getDate() + "/" + (d.getMonth() + 1 < 10 ? "0" + (d.getMonth() + 1) : d.getMonth()) + "/" + d.getFullYear();
  };

  const hasMoreReviews = () => {
    return reviews.length > defaultReviews;
  }

  return (
    <ReviewsContain>
      {messagesQuery.data && <h5 className="title"> {messagesQuery.data.messages["reviews"]}</h5>}
      {
        product.hasProductReviews && <><OverallRating>
          <div>
            <span className="overall-rating">{product.overallReviewRating}</span>
            <div className="overall-stars">
              <StarRating rating={product.overallReviewRating} size={32} />
              <small>({product.productReviewsCount})</small>
            </div>
          </div>
        </OverallRating>

          <Reviews>

            { messagesQuery.data && hasDifferentLangReviews && !showAllLangs &&
              <p onClick={handleShowAllLangs} style={{cursor: 'pointer', textDecoration: 'underline'}}>
                { messagesQuery.data.messages["view-Diff-Lang-Reviews"] } ({ product.productReviews.length - filterReviewsByLanguage(countryLang?.slice(3, 5), product.productReviews).length })
              </p>
            }

            {
              reviews.slice(0, defaultReviews).map(r => (
                <div key={`review-${r.reviewID}`} className="review">
                  <div className="left-side">
                    <div className="user-info">
                      <span className="image">{r.customerDisplayName.charAt(0)}</span>
                      <div>
                        <p className="name">{r.customerDisplayName}</p>
                        <p className="date">{getDate(r.reviewDate)}</p>
                      </div>
                    </div>
                    <div className="rating">
                      <StarRating rating={r.overallRating} size={24} />
                    </div>
                    <div className="review-content">
                      <p className="review-text">{r.reviewText}</p>
                    </div>
                  </div>
                  <div className="right-side">
                    <div className="individual-ratings">
                      {
                        r.featureRatings.map( (featureRating:any) => (
                          <div key={`rating-${r.reviewID}-${featureRating.featureID}`} className="individual-rating">
                            {messagesQuery.data && <span
                              className="name">{messagesQuery.data.messages[featureRating.name.messageKey]}</span>}
                            <StarRating rating={featureRating.rating} size={20} />
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>
              ))
            }
          </Reviews>
          {
            messagesQuery.data && hasMoreReviews() && <SeeAllReviews>
              <Button variant={"primary"} onClick={() => {
                const loadMoreQuantity = showAllLangs ? product.productReviews.length : websiteLangReviews.length;
                setDefaultReviews(loadMoreQuantity);
              }}>{ messagesQuery.data.messages["load-more"] } ({ reviews.length - defaultReviews })</Button>
            </SeeAllReviews>
          }
        </>
      }

    </ReviewsContain>
  )
}

export default ProductReviews;
